import React, { useState, useEffect, useRef, useCallback } from 'react';
import Sidebar from '../../../components/Sidebar';
import MeditationNav from '../MeditationNav';
import {
  HiMenu,
  HiSearch,
  HiPlus,
  HiChevronDown,
  HiChevronRight,
  HiInformationCircle,
} from 'react-icons/hi';
import { MdDelete, MdEdit } from 'react-icons/md';
import Toast from '../../../components/toaster';
import { uploadImage } from '../../../services/assetService';
import EditOrderIcon from '../../../assets/icons/edit-order.svg';
import {
  DndContext,
  closestCenter,
} from '@dnd-kit/core';
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
  arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  listMeditationMainCategoriesWithSubcategories,
  createMeditationMainCategory,
  updateMeditationMainCategory,
  getMeditationMainCategory,
  deleteMeditationMainCategory,
  reorderMeditationMainCategories,
  createMeditationSubCategory,
  updateMeditationSubCategory,
  getMeditationSubCategory,
  deleteMeditationSubCategory,
  listMeditationSubCategories,
  reorderMeditationSubCategories,
} from '../../../services/meditationService';

/** Drag-handle grid icon — matches Practice Modules / FAQ reorder UI */
const GridIcon = ({ size = 18 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 18 18"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className="text-gray-400"
  >
    <circle cx="4.5" cy="3" r="1.5" fill="currentColor" />
    <circle cx="4.5" cy="9" r="1.5" fill="currentColor" />
    <circle cx="4.5" cy="15" r="1.5" fill="currentColor" />
    <circle cx="13.5" cy="3" r="1.5" fill="currentColor" />
    <circle cx="13.5" cy="9" r="1.5" fill="currentColor" />
    <circle cx="13.5" cy="15" r="1.5" fill="currentColor" />
  </svg>
);

const SortableReorderItem = ({ id, title, index, image }) => {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };
  const initial = (title || '?').charAt(0);

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      className="flex items-center justify-between p-4 bg-[#FDF6EF] rounded-xl border border-white/10 cursor-grab active:cursor-grabbing touch-none"
    >
      <div className="flex items-center gap-4 flex-1 min-w-0">
        <div className="text-black flex items-center justify-center text-sm font-semibold min-w-[20px]">
          {index + 1}
        </div>
        {image ? (
          <img
            src={image}
            alt=""
            className="w-10 h-10 rounded-lg object-cover flex-shrink-0"
          />
        ) : (
          <div className="w-10 h-10 rounded-lg bg-brand text-black flex items-center justify-center font-bold flex-shrink-0">
            {initial}
          </div>
        )}
        <p className="text-black font-medium flex-1 min-w-0 break-words line-clamp-2">
          {title}
        </p>
      </div>
      <div className="flex-shrink-0 ml-2">
        <GridIcon size={18} />
      </div>
    </div>
  );
};

const sortByOrder = (items) =>
  [...(items || [])].sort((a, b) => (a.order || 0) - (b.order || 0));

const Spinner = () => (
  <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
  </svg>
);

const FullPageSpinner = () => (
  <div className="fixed inset-0 ml-0 lg:ml-64 flex items-center justify-center z-40">
    <div className="flex items-center gap-3" role="status" aria-live="polite">
      <svg className="w-12 h-12 animate-spin" viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg">
        <defs>
          <linearGradient id="med-main-gradient" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#E9D5FF" />
            <stop offset="50%" stopColor="#A855F7" />
            <stop offset="100%" stopColor="#6B21A8" />
          </linearGradient>
        </defs>
        <circle cx="50" cy="50" r="40" stroke="url(#med-main-gradient)" strokeWidth="6" strokeLinecap="round" strokeDasharray="60 200" fill="none" transform="rotate(-90 50 50)" />
      </svg>
    </div>
  </div>
);

const StatusBadge = ({ isActive }) => (
  <span
    className={`px-2 py-1 text-xs font-semibold rounded-lg ${
      isActive ? 'bg-[#9FF7C9]/30 text-[#60BC7C]' : 'bg-[#FFF0F0] text-[#F76464]'
    }`}
  >
    {isActive ? 'Active' : 'Inactive'}
  </span>
);

/** Row thumbnail — renders only when a valid image URL exists. */
const ListRowThumb = ({ src, alt = '' }) => {
  const [failed, setFailed] = useState(false);
  const trimmed = String(src || '').trim();
  if (!trimmed || failed || !/^https?:\/\/.+/i.test(trimmed)) return null;
  return (
    <img
      src={trimmed}
      alt={alt}
      className="h-9 w-9 flex-shrink-0 rounded-lg object-cover border border-gray-200 bg-gray-50"
      onError={() => setFailed(true)}
    />
  );
};

const HTTP_URL_RE = /^https?:\/\/.+/i;

const normalizeImageUrl = (value) => String(value || '').trim();

/** Validate optional image URL: empty OK; non-empty must be http/https. */
const getImageFieldError = (value) => {
  const trimmed = normalizeImageUrl(value);
  if (!trimmed) return '';
  if (!HTTP_URL_RE.test(trimmed)) return 'Image URL must be a valid HTTP or HTTPS URL';
  return '';
};

/**
 * Attach image to create/update payload.
 * - create: include trimmed URL or null
 * - update: omit when unchanged (preserve); send null/URL when changed or cleared
 */
const applyImageToPayload = (payload, { current, original, isCreate }) => {
  const next = normalizeImageUrl(current);
  if (isCreate) {
    payload.image = next || null;
    return;
  }
  const prev = normalizeImageUrl(original);
  if (next === prev) return; // omit → backend preserves
  payload.image = next || null;
};

/** Optional string fields (description/note): empty ↔ null for compare/clear. */
const normalizeOptionalText = (value) => String(value ?? '').trim();

/**
 * Attach optional text to create/update payload (Backend description/note rules).
 * - create: trimmed string or null (omit empty → null)
 * - update: omit when unchanged (preserve); send null when cleared; else trimmed string
 */
const applyOptionalTextToPayload = (payload, key, { current, original, isCreate }) => {
  const next = normalizeOptionalText(current);
  if (isCreate) {
    payload[key] = next || null;
    return;
  }
  const prev = normalizeOptionalText(original);
  if (next === prev) return; // omit → backend preserves
  payload[key] = next || null;
};

/**
 * Optional category image: Gift Cards–style file/URL toggle using shared uploadImage.
 * Mode switches do not clear the current URL. Upload failure keeps the previous value.
 */
const CategoryImageField = ({
  id,
  value,
  error,
  onChange,
  onClear,
  onUploadingChange,
  disabled = false,
  resetKey = 0,
}) => {
  const [imageSource, setImageSource] = useState('url'); // 'url' | 'file'
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [previewFailed, setPreviewFailed] = useState(false);
  const fileInputRef = useRef(null);

  const trimmed = normalizeImageUrl(value);
  const canPreview = trimmed && HTTP_URL_RE.test(trimmed) && !error;

  useEffect(() => {
    setImageSource('url');
    setUploading(false);
    setUploadError('');
    setPreviewFailed(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  }, [resetKey]);

  useEffect(() => {
    setPreviewFailed(false);
  }, [trimmed]);

  useEffect(() => {
    onUploadingChange?.(uploading);
  }, [uploading, onUploadingChange]);

  const switchSource = (next) => {
    if (uploading || disabled) return;
    setImageSource(next);
    setUploadError('');
    if (fileInputRef.current) fileInputRef.current.value = '';
    // Keep current value — do not clear on mode switch
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file || uploading || disabled) return;

    setUploading(true);
    setUploadError('');
    try {
      const uploadedUrl = await uploadImage(file);
      onChange(uploadedUrl);
    } catch (err) {
      // Preserve previously saved / entered image and other form values
      setUploadError(err.message || 'Failed to upload image. Please try again.');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleClear = () => {
    if (uploading || disabled) return;
    setUploadError('');
    if (fileInputRef.current) fileInputRef.current.value = '';
    onClear();
  };

  const showClear = Boolean(trimmed) || Boolean(uploadError);

  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <label htmlFor={id} className="block text-xs font-medium text-gray-700">
          Image <span className="font-normal text-gray-400">(optional)</span>
        </label>
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1 group relative">
            <HiInformationCircle className="w-4 h-4 text-gray-400 cursor-pointer" />
            <span
              className={`absolute right-0 top-0 -mt-12 sm:-mt-8 px-2 py-1.5 bg-gray-800 text-white text-xs rounded shadow-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10 sm:whitespace-nowrap leading-tight inline-block ${
                imageSource === 'url'
                  ? 'w-[185px] sm:w-fit sm:max-w-[290px]'
                  : 'w-[175px] sm:w-fit sm:max-w-[240px]'
              }`}
            >
              {imageSource === 'url'
                ? 'Switch left to choose a file from your device'
                : 'Switch right to paste an image URL.'}
            </span>
          </div>
          <button
            type="button"
            aria-label={
              imageSource === 'url' ? 'Switch to file upload' : 'Switch to image URL'
            }
            disabled={uploading || disabled}
            onClick={() => switchSource(imageSource === 'url' ? 'file' : 'url')}
            className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors focus:outline-none disabled:opacity-50 ${
              imageSource === 'url' ? 'bg-brand' : 'bg-gray-200'
            }`}
          >
            <span
              className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform shadow-sm ${
                imageSource === 'url' ? 'translate-x-5' : 'translate-x-0.5'
              }`}
            />
          </button>
        </div>
      </div>

      {imageSource === 'url' ? (
        <div>
          <div className="flex gap-2">
            <input
              id={id}
              type="url"
              value={value}
              disabled={uploading || disabled}
              onChange={(e) => {
                setUploadError('');
                onChange(e.target.value);
              }}
              placeholder="https://…"
              className={`flex-1 px-3 py-2 text-sm border rounded-lg focus:outline-none disabled:opacity-50 ${
                error ? 'border-red-300' : 'border-gray-300 focus:border-gray-500'
              }`}
            />
            {showClear && (
              <button
                type="button"
                onClick={handleClear}
                disabled={uploading || disabled}
                className="px-3 py-2 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 whitespace-nowrap disabled:opacity-50"
              >
                Clear
              </button>
            )}
          </div>
          <p className="mt-1 text-xs text-gray-400">
            Optional HTTP/HTTPS URL. Clear removes a saved image.
          </p>
        </div>
      ) : (
        <div>
          <div className="flex gap-2">
            <input
              ref={fileInputRef}
              id={`${id}-file`}
              type="file"
              accept="image/*"
              disabled={uploading || disabled}
              onChange={handleFileSelect}
              className={`flex-1 px-3 py-2 text-sm border rounded-lg focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed ${
                uploadError || error ? 'border-red-300' : 'border-gray-300'
              }`}
            />
            {showClear && (
              <button
                type="button"
                onClick={handleClear}
                disabled={uploading || disabled}
                className="px-3 py-2 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 whitespace-nowrap disabled:opacity-50"
              >
                Clear
              </button>
            )}
          </div>
          {uploading && (
            <div className="mt-2 flex items-center gap-2" role="status" aria-live="polite">
              <svg
                className="animate-spin h-4 w-4 text-brand"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
              </svg>
              <span className="text-xs text-gray-600">Uploading image...</span>
            </div>
          )}
          <p className="mt-1 text-xs text-gray-400">
            Optional image file. Uses the shared Admin upload helper.
          </p>
        </div>
      )}

      {uploadError && <p className="mt-1 text-xs text-red-600">{uploadError}</p>}
      {error && !uploadError && <p className="mt-1 text-xs text-red-600">{error}</p>}

      {canPreview && !previewFailed && !uploading && (
        <div className="mt-2">
          <img
            src={trimmed}
            alt="Category preview"
            className="h-20 w-20 object-cover rounded-lg border border-gray-200 bg-gray-50"
            onError={() => setPreviewFailed(true)}
          />
        </div>
      )}
      {canPreview && previewFailed && !uploading && (
        <p className="mt-2 text-xs text-amber-700">Preview unavailable — URL may be broken.</p>
      )}
    </div>
  );
};

const emptyMainForm = () => ({
  title: '',
  description: '',
  image: '',
  is_active: true,
});

const emptySubForm = (mainCategoryId = '') => ({
  main_category_id: mainCategoryId,
  title: '',
  description: '',
  note: '',
  image: '',
  is_active: true,
});

const MeditationMainCategories = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [mains, setMains] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [listMutationLoading, setListMutationLoading] = useState(false);
  const [error, setError] = useState(null);
  const [expandedIds, setExpandedIds] = useState(() => new Set());

  // Main form
  const [showMainForm, setShowMainForm] = useState(false);
  const [mainEditing, setMainEditing] = useState(false);
  const [mainSelected, setMainSelected] = useState(null);
  const [mainForm, setMainForm] = useState(emptyMainForm());
  const [mainFormErrors, setMainFormErrors] = useState({});
  const [mainFormGeneralError, setMainFormGeneralError] = useState('');
  const [mainSaving, setMainSaving] = useState(false);
  const [mainDetailLoading, setMainDetailLoading] = useState(false);
  const [mainImageUploading, setMainImageUploading] = useState(false);
  const [mainImageResetKey, setMainImageResetKey] = useState(0);

  // Sub form
  const [showSubForm, setShowSubForm] = useState(false);
  const [subEditing, setSubEditing] = useState(false);
  const [subSelected, setSubSelected] = useState(null);
  const [subForm, setSubForm] = useState(emptySubForm());
  const [subFormErrors, setSubFormErrors] = useState({});
  const [subFormGeneralError, setSubFormGeneralError] = useState('');
  const [subSaving, setSubSaving] = useState(false);
  const [subDetailLoading, setSubDetailLoading] = useState(false);
  const [subImageUploading, setSubImageUploading] = useState(false);
  const [subImageResetKey, setSubImageResetKey] = useState(0);

  const handleMainImageUploading = useCallback((busy) => {
    setMainImageUploading(Boolean(busy));
  }, []);
  const handleSubImageUploading = useCallback((busy) => {
    setSubImageUploading(Boolean(busy));
  }, []);

  // Confirm modal: cascade deactivate / soft-delete
  const [confirmState, setConfirmState] = useState(null);
  // { kind: 'main-cascade' | 'sub-deactivate', item, mode: 'delete' | 'update', payload?: object }
  const [confirming, setConfirming] = useState(false);

  // Main categories reorder (DnD modal — Practice Modules pattern)
  const [showMainReorderModal, setShowMainReorderModal] = useState(false);
  const [reorderMains, setReorderMains] = useState([]);
  const [mainReorderSaving, setMainReorderSaving] = useState(false);

  // Subcategories reorder scoped to one Main
  const [showSubReorderModal, setShowSubReorderModal] = useState(false);
  const [reorderSubs, setReorderSubs] = useState([]);
  const [reorderSubParent, setReorderSubParent] = useState(null);
  const [subReorderSaving, setSubReorderSaving] = useState(false);

  const searchTimerRef = useRef(null);
  const listRequestIdRef = useRef(0);
  const isInitialDebounceRef = useRef(true);
  const mainReorderSavingRef = useRef(false);
  const subReorderSavingRef = useRef(false);

  // Restore body overflow when reorder modals close
  useEffect(() => {
    if (!showMainReorderModal && !showSubReorderModal) {
      document.body.style.overflowX = '';
    }
    return () => {
      document.body.style.overflowX = '';
    };
  }, [showMainReorderModal, showSubReorderModal]);

  const buildListBody = useCallback((searchText) => {
    const body = {};
    if (searchText) body.searchtext = searchText;
    // Always list all statuses — no is_active filter
    return body;
  }, []);

  const loadTree = useCallback(async (searchText = '') => {
    const reqId = ++listRequestIdRef.current;
    setError(null);
    try {
      const res = await listMeditationMainCategoriesWithSubcategories(
        buildListBody(searchText)
      );
      if (reqId !== listRequestIdRef.current) return;
      const items = Array.isArray(res?.data) ? res.data : [];
      setMains(items);
      setTotalCount(
        typeof res?.totalCount === 'number' ? res.totalCount : items.length
      );
    } catch (err) {
      if (reqId !== listRequestIdRef.current) return;
      setError(err.message || 'Error loading meditation categories.');
      setMains([]);
      setTotalCount(0);
    } finally {
      if (reqId === listRequestIdRef.current) {
        setIsLoading(false);
        setListMutationLoading(false);
      }
    }
  }, [buildListBody]);

  useEffect(() => {
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    if (isInitialDebounceRef.current) {
      isInitialDebounceRef.current = false;
      return;
    }
    searchTimerRef.current = setTimeout(() => {
      setDebouncedSearch(searchQuery);
    }, 400);
    return () => clearTimeout(searchTimerRef.current);
  }, [searchQuery]);

  useEffect(() => {
    setIsLoading(true);
    loadTree(debouncedSearch);
  }, [debouncedSearch, loadTree]);

  const refreshTree = async () => {
    setListMutationLoading(true);
    await loadTree(debouncedSearch);
  };

  const toggleExpand = (mainId) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(mainId)) next.delete(mainId);
      else next.add(mainId);
      return next;
    });
  };

  // ----- Main form -----
  const openAddMain = () => {
    setMainForm(emptyMainForm());
    setMainFormErrors({});
    setMainFormGeneralError('');
    setMainSelected(null);
    setMainEditing(false);
    setMainImageUploading(false);
    setMainImageResetKey((k) => k + 1);
    setShowMainForm(true);
  };

  const openEditMain = async (item) => {
    setMainFormErrors({});
    setMainFormGeneralError('');
    setMainEditing(true);
    setMainSelected(item);
    setMainForm({
      title: item.title || '',
      description: item.description || '',
      image: item.image || '',
      is_active: item.is_active !== false,
    });
    setMainImageUploading(false);
    setMainImageResetKey((k) => k + 1);
    setShowMainForm(true);
    setMainDetailLoading(true);
    try {
      const res = await getMeditationMainCategory(item._id);
      const doc = res?.data || res;
      if (doc && doc._id) {
        setMainSelected(doc);
        setMainForm({
          title: doc.title || '',
          description: doc.description || '',
          image: doc.image || '',
          is_active: doc.is_active !== false,
        });
      }
    } catch (err) {
      Toast.error(err.message || 'Failed to load main category detail');
    } finally {
      setMainDetailLoading(false);
    }
  };

  const closeMainForm = () => {
    if (mainSaving || mainImageUploading) return;
    setShowMainForm(false);
    setMainImageUploading(false);
    setMainForm(emptyMainForm());
    setMainFormErrors({});
    setMainFormGeneralError('');
    setMainSelected(null);
    setMainEditing(false);
  };

  const validateMainForm = () => {
    const errs = {};
    if (!String(mainForm.title || '').trim()) errs.title = 'Title is required';
    const imageErr = getImageFieldError(mainForm.image);
    if (imageErr) errs.image = imageErr;
    return errs;
  };

  const submitMainUpdate = async (payload) => {
    await updateMeditationMainCategory(payload);
  };

  const handleMainSave = async () => {
    if (mainImageUploading) return;
    const errs = validateMainForm();
    if (Object.keys(errs).length) {
      setMainFormErrors(errs);
      return;
    }

    if (mainEditing && mainSelected) {
      const wasActive = mainSelected.is_active !== false;
      const willDeactivate = wasActive && mainForm.is_active === false;

      if (willDeactivate) {
        // Omit order — backend preserves existing value
        const cascadePayload = {
          main_category_id: mainSelected._id,
          title: mainForm.title.trim(),
          description: mainForm.description,
          is_active: false,
        };
        applyImageToPayload(cascadePayload, {
          current: mainForm.image,
          original: mainSelected?.image,
          isCreate: false,
        });
        setConfirmState({
          kind: 'main-cascade',
          item: mainSelected,
          mode: 'update',
          payload: cascadePayload,
        });
        return;
      }

      setMainSaving(true);
      setMainFormErrors({});
      setMainFormGeneralError('');
      try {
        // Omit order — backend preserves existing value
        const updatePayload = {
          main_category_id: mainSelected._id,
          title: mainForm.title.trim(),
          description: mainForm.description,
          is_active: mainForm.is_active,
        };
        applyImageToPayload(updatePayload, {
          current: mainForm.image,
          original: mainSelected?.image,
          isCreate: false,
        });
        await submitMainUpdate(updatePayload);
        const reactivated =
          mainSelected.is_active === false && mainForm.is_active === true;
        closeMainForm();
        await refreshTree();
        if (reactivated) {
          Toast.warning(
            'Main category reactivated. Subcategories were not automatically reactivated — refresh shows their actual status.'
          );
        } else {
          Toast.success('Main category updated successfully');
        }
      } catch (err) {
        if (err.status === 503) {
          Toast.error(
            err.message ||
              'Deactivation unavailable (transactions not ready). No changes were saved.'
          );
          setMainFormGeneralError(err.message);
        } else {
          setMainFormGeneralError(err.message || 'Failed to update main category');
        }
      } finally {
        setMainSaving(false);
      }
      return;
    }

    setMainSaving(true);
    setMainFormErrors({});
    setMainFormGeneralError('');
    try {
      // Omit order — backend assigns max(order) + 1
      const payload = {
        title: mainForm.title.trim(),
        is_active: mainForm.is_active,
      };
      if (mainForm.description !== undefined) payload.description = mainForm.description;
      applyImageToPayload(payload, {
        current: mainForm.image,
        original: null,
        isCreate: true,
      });
      await createMeditationMainCategory(payload);
      Toast.success('Main category created successfully');
      closeMainForm();
      await refreshTree();
    } catch (err) {
      setMainFormGeneralError(err.message || 'Failed to create main category');
    } finally {
      setMainSaving(false);
    }
  };

  const openMainSoftDelete = (item) => {
    setConfirmState({
      kind: 'main-cascade',
      item,
      mode: 'delete',
    });
  };

  // ----- Sub form -----
  const openAddSub = (main) => {
    setSubForm(emptySubForm(main._id));
    setSubFormErrors({});
    setSubFormGeneralError('');
    setSubSelected(null);
    setSubEditing(false);
    setSubImageUploading(false);
    setSubImageResetKey((k) => k + 1);
    setShowSubForm(true);
    setExpandedIds((prev) => new Set(prev).add(main._id));
  };

  const openEditSub = async (sub, parentMain) => {
    setSubFormErrors({});
    setSubFormGeneralError('');
    setSubEditing(true);
    setSubSelected(sub);
    setSubForm({
      main_category_id: sub.main_category_id || parentMain?._id || '',
      title: sub.title || '',
      description: sub.description || '',
      note: sub.note || '',
      image: sub.image || '',
      is_active: sub.is_active !== false,
    });
    setSubImageUploading(false);
    setSubImageResetKey((k) => k + 1);
    setShowSubForm(true);
    setSubDetailLoading(true);
    try {
      const res = await getMeditationSubCategory(sub._id);
      const doc = res?.data || res;
      if (doc && doc._id) {
        setSubSelected(doc);
        setSubForm({
          main_category_id: doc.main_category_id || '',
          title: doc.title || '',
          description: doc.description || '',
          note: doc.note || '',
          image: doc.image || '',
          is_active: doc.is_active !== false,
        });
      }
    } catch (err) {
      Toast.error(err.message || 'Failed to load subcategory detail');
    } finally {
      setSubDetailLoading(false);
    }
  };

  const closeSubForm = () => {
    if (subSaving || subImageUploading) return;
    setShowSubForm(false);
    setSubImageUploading(false);
    setSubForm(emptySubForm());
    setSubFormErrors({});
    setSubFormGeneralError('');
    setSubSelected(null);
    setSubEditing(false);
  };

  const validateSubForm = () => {
    const errs = {};
    if (!String(subForm.title || '').trim()) errs.title = 'Title is required';
    if (!subForm.main_category_id) errs.main_category_id = 'Parent main category is required';
    const imageErr = getImageFieldError(subForm.image);
    if (imageErr) errs.image = imageErr;
    return errs;
  };

  const handleSubSave = async () => {
    if (subImageUploading) return;
    const errs = validateSubForm();
    if (Object.keys(errs).length) {
      setSubFormErrors(errs);
      return;
    }

    if (subEditing && subSelected) {
      const wasActive = subSelected.is_active !== false;
      const willDeactivate = wasActive && subForm.is_active === false;
      if (willDeactivate) {
        // Omit order and main_category_id — preserve parent + order
        const deactivatePayload = {
          sub_category_id: subSelected._id,
          title: subForm.title.trim(),
          description: subForm.description,
          is_active: false,
        };
        applyOptionalTextToPayload(deactivatePayload, 'note', {
          current: subForm.note,
          original: subSelected?.note,
          isCreate: false,
        });
        applyImageToPayload(deactivatePayload, {
          current: subForm.image,
          original: subSelected?.image,
          isCreate: false,
        });
        setConfirmState({
          kind: 'sub-deactivate',
          item: subSelected,
          mode: 'update',
          payload: deactivatePayload,
        });
        return;
      }

      setSubSaving(true);
      setSubFormErrors({});
      setSubFormGeneralError('');
      try {
        // Omit order and main_category_id — preserve parent + order
        const updatePayload = {
          sub_category_id: subSelected._id,
          title: subForm.title.trim(),
          description: subForm.description,
          is_active: subForm.is_active,
        };
        applyOptionalTextToPayload(updatePayload, 'note', {
          current: subForm.note,
          original: subSelected?.note,
          isCreate: false,
        });
        applyImageToPayload(updatePayload, {
          current: subForm.image,
          original: subSelected?.image,
          isCreate: false,
        });
        await updateMeditationSubCategory(updatePayload);
        Toast.success('Subcategory updated successfully');
        closeSubForm();
        await refreshTree();
      } catch (err) {
        setSubFormGeneralError(err.message || 'Failed to update subcategory');
      } finally {
        setSubSaving(false);
      }
      return;
    }

    setSubSaving(true);
    setSubFormErrors({});
    setSubFormGeneralError('');
    try {
      // Omit order — backend assigns max(order) + 1 within parent Main
      const createPayload = {
        main_category_id: subForm.main_category_id,
        title: subForm.title.trim(),
        description: subForm.description,
        is_active: subForm.is_active,
      };
      applyOptionalTextToPayload(createPayload, 'note', {
        current: subForm.note,
        original: null,
        isCreate: true,
      });
      applyImageToPayload(createPayload, {
        current: subForm.image,
        original: null,
        isCreate: true,
      });
      await createMeditationSubCategory(createPayload);
      Toast.success('Subcategory created successfully');
      const parentId = subForm.main_category_id;
      closeSubForm();
      setExpandedIds((prev) => new Set(prev).add(parentId));
      await refreshTree();
    } catch (err) {
      setSubFormGeneralError(err.message || 'Failed to create subcategory');
    } finally {
      setSubSaving(false);
    }
  };

  const openSubSoftDelete = (sub) => {
    setConfirmState({
      kind: 'sub-deactivate',
      item: sub,
      mode: 'delete',
    });
  };

  // Search filters the tree — reorder must use the full unfiltered set
  const searchActive = Boolean(String(searchQuery || '').trim() || debouncedSearch);

  // ----- Reorder: Main Categories -----
  const openMainReorder = () => {
    if (mainReorderSaving || !mains.length || searchActive) return;
    setReorderMains(sortByOrder(mains));
    setShowMainReorderModal(true);
  };

  const closeMainReorder = () => {
    if (mainReorderSaving) return;
    setShowMainReorderModal(false);
    setReorderMains([]);
  };

  const handleSaveMainOrder = async () => {
    if (mainReorderSavingRef.current || !reorderMains.length) return;
    mainReorderSavingRef.current = true;
    setMainReorderSaving(true);
    try {
      // Single bulk request — IDs + order only (avoids cascade / status side effects)
      await reorderMeditationMainCategories({
        categories: reorderMains.map((item, index) => ({
          main_category_id: item._id,
          order: index + 1,
        })),
      });
      setShowMainReorderModal(false);
      setReorderMains([]);
      await refreshTree();
      Toast.success('Main category order updated successfully');
    } catch (err) {
      Toast.error(err.message || 'Failed to save main category order');
      setShowMainReorderModal(false);
      setReorderMains([]);
      await refreshTree();
    } finally {
      mainReorderSavingRef.current = false;
      setMainReorderSaving(false);
    }
  };

  // ----- Reorder: Subcategories (scoped to one Main) -----
  const openSubReorder = async (main) => {
    if (subReorderSaving || !main?._id) return;
    setReorderSubParent(main);
    setSubReorderSaving(true);
    try {
      // Load complete subcategory list for this parent (not a search-filtered subset)
      const res = await listMeditationSubCategories({
        main_category_id: main._id,
      });
      const rows = Array.isArray(res?.data) ? res.data : [];
      if (!rows.length) {
        Toast.error('No subcategories found for this main category');
        setReorderSubParent(null);
        return;
      }
      setReorderSubs(sortByOrder(rows));
      setShowSubReorderModal(true);
    } catch (err) {
      Toast.error(err.message || 'Failed to load subcategories for reordering');
      setReorderSubParent(null);
      setReorderSubs([]);
    } finally {
      setSubReorderSaving(false);
    }
  };

  const closeSubReorder = () => {
    if (subReorderSaving) return;
    setShowSubReorderModal(false);
    setReorderSubs([]);
    setReorderSubParent(null);
  };

  const handleSaveSubOrder = async () => {
    if (subReorderSavingRef.current || !reorderSubs.length || !reorderSubParent?._id) {
      return;
    }
    subReorderSavingRef.current = true;
    setSubReorderSaving(true);
    try {
      // Single bulk request — parent scope + IDs + order only
      await reorderMeditationSubCategories({
        main_category_id: reorderSubParent._id,
        subcategories: reorderSubs.map((item, index) => ({
          sub_category_id: item._id,
          order: index + 1,
        })),
      });
      setShowSubReorderModal(false);
      setReorderSubs([]);
      setReorderSubParent(null);
      await refreshTree();
      Toast.success('Subcategory order updated successfully');
    } catch (err) {
      Toast.error(err.message || 'Failed to save subcategory order');
      setShowSubReorderModal(false);
      setReorderSubs([]);
      setReorderSubParent(null);
      await refreshTree();
    } finally {
      subReorderSavingRef.current = false;
      setSubReorderSaving(false);
    }
  };

  // ----- Confirm handlers -----
  const closeConfirm = () => {
    if (confirming) return;
    setConfirmState(null);
  };

  const handleConfirm = async () => {
    if (!confirmState?.item) return;
    setConfirming(true);
    try {
      if (confirmState.kind === 'main-cascade') {
        const cascadeItem = confirmState.item;
        let cascadeRes;
        if (confirmState.mode === 'delete') {
          cascadeRes = await deleteMeditationMainCategory(cascadeItem._id);
        } else {
          cascadeRes = await updateMeditationMainCategory(confirmState.payload);
        }
        // Success envelope data is cascade payload, not a full Main document
        const cascadeData = cascadeRes?.data;
        const subCount =
          typeof cascadeData?.subcategory_count === 'number'
            ? cascadeData.subcategory_count
            : Array.isArray(cascadeItem?.sub_categories)
              ? cascadeItem.sub_categories.length
              : null;
        setConfirmState(null);
        setShowMainForm(false);
        setMainForm(emptyMainForm());
        setMainSelected(null);
        setMainEditing(false);
        await refreshTree();
        Toast.warning(
          typeof subCount === 'number'
            ? `Main category deactivated. ${subCount} subcategory(ies) were also deactivated.`
            : 'Main category deactivated. Its subcategories were also deactivated.'
        );
      } else if (confirmState.kind === 'sub-deactivate') {
        if (confirmState.mode === 'delete') {
          await deleteMeditationSubCategory(confirmState.item._id);
        } else {
          await updateMeditationSubCategory(confirmState.payload);
        }
        setConfirmState(null);
        setShowSubForm(false);
        setSubForm(emptySubForm());
        setSubSelected(null);
        setSubEditing(false);
        await refreshTree();
        Toast.success('Subcategory deactivated successfully');
      }
    } catch (err) {
      if (err.status === 503) {
        Toast.error(
          err.message ||
            'Deactivation unavailable: MongoDB transactions are not ready on this deployment. No changes were saved.'
        );
        // Do not close forms or mark inactive — leave UI as-is
      } else {
        Toast.error(err.message || 'Action failed');
      }
    } finally {
      setConfirming(false);
    }
  };

  const showLoading = isLoading || listMutationLoading;
  const hasFilters = Boolean(debouncedSearch);

  return (
    <div className="h-screen flex overflow-hidden">
      <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />
      <div className="flex-1 bg-primary flex flex-col px-4 py-4 md:p-6 overflow-hidden">
        <div className="flex-1 flex flex-col min-h-0 overflow-hidden">
          {/* Header */}
          <div className="mb-4 md:mb-6">
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => setSidebarOpen(true)}
                className="lg:hidden p-2 rounded-lg hover:bg-white/50 transition-colors"
                aria-label="Open menu"
              >
                <HiMenu className="w-6 h-6 text-gray-800" />
              </button>
              <div>
                <h1 className="text-xl md:text-3xl font-bold text-gray-800">Meditation</h1>
                <p className="text-sm text-gray-500 mt-0.5">Main Categories &amp; Subcategories</p>
              </div>
            </div>
          </div>

          <MeditationNav />

          {/* Filters */}
          <div className="mb-4 md:mb-6 flex-shrink-0">
            <div className="flex flex-col md:flex-row md:justify-between md:items-end gap-3 md:gap-4">
              <div className="flex flex-col sm:flex-row gap-3 w-full md:w-auto md:flex-1 min-w-0">
                <div className="w-full sm:w-64 relative flex-shrink-0">
                  <HiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-450 w-4 h-4" />
                  <input
                    type="text"
                    placeholder="Search title, description, or note..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full pl-9 pr-3 py-2 md:py-1.5 text-sm border border-[#ECECEC] rounded-xl focus:outline-none bg-white"
                    aria-label="Search main categories and subcategories"
                  />
                </div>
              </div>
              <div className="flex flex-col items-start md:items-end gap-1 flex-shrink-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <button
                    type="button"
                    onClick={openMainReorder}
                    disabled={!mains.length || mainReorderSaving || searchActive}
                    title={
                      searchActive
                        ? 'Clear search to reorder all main categories'
                        : 'Reorder all main categories'
                    }
                    className="inline-flex items-center gap-2 px-3 py-2 text-sm font-semibold text-white rounded-xl shadow-md hover:shadow-lg transition-all bg-brand hover:opacity-90 whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
                    aria-label={
                      searchActive
                        ? 'Reorder unavailable while search is active'
                        : 'Reorder main categories'
                    }
                  >
                    <img
                      src={EditOrderIcon}
                      alt=""
                      className="w-5 h-5"
                      style={{ filter: 'brightness(0) invert(1)' }}
                    />
                    Reorder
                  </button>
                  {(totalCount > 0 || hasFilters || Boolean(error)) && (
                    <button
                      type="button"
                      onClick={openAddMain}
                      className="inline-flex items-center gap-2 px-3 py-2 text-sm font-semibold text-white rounded-xl shadow-md hover:shadow-lg transition-all bg-brand hover:opacity-90 whitespace-nowrap"
                    >
                      <HiPlus className="w-5 h-5" />
                      Add Main Category
                    </button>
                  )}
                </div>
                {searchActive && (
                  <p className="text-xs text-amber-700">
                    Clear search to reorder the full main category list.
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Content */}
          {showLoading ? (
            <FullPageSpinner />
          ) : totalCount === 0 && !error ? (
            <div className="flex-1 min-h-0 flex items-center justify-center px-4">
              <div className="text-center text-gray-500 max-w-md">
                <h3 className="text-xl font-semibold text-gray-800 mb-2">
                  {hasFilters ? 'No matching categories' : 'No main categories yet'}
                </h3>
                <p className="text-sm text-gray-500 mb-6">
                  {hasFilters
                    ? 'Try adjusting search or status filters'
                    : 'Create a main category, then add subcategories under it'}
                </p>
                {!hasFilters && (
                  <button
                    type="button"
                    onClick={openAddMain}
                    className="inline-flex items-center gap-2 px-6 py-2.5 text-sm font-semibold text-white rounded-xl shadow-md bg-brand hover:opacity-90"
                  >
                    <HiPlus className="w-4 h-4" /> Add Main Category
                  </button>
                )}
              </div>
            </div>
          ) : (
            <div className="bg-white rounded-xl shadow-sm overflow-hidden flex flex-col flex-1 min-h-0">
              {error ? (
                <div className="px-4 py-6 text-center">
                  <p className="text-red-500 mb-3">{error}</p>
                  <button
                    type="button"
                    onClick={() => {
                      setIsLoading(true);
                      loadTree(debouncedSearch);
                    }}
                    className="px-4 py-2 text-sm font-medium text-white bg-brand rounded-lg hover:opacity-90"
                  >
                    Retry
                  </button>
                </div>
              ) : (
                <div className="flex-1 min-h-0 overflow-y-auto divide-y divide-gray-100">
                  {mains.map((main) => {
                    const mainId = main._id;
                    const expanded = expandedIds.has(mainId);
                    const subs = Array.isArray(main.sub_categories) ? main.sub_categories : [];
                    const panelId = `main-subs-${mainId}`;
                    return (
                      <div key={mainId} className="bg-white">
                        <div
                          role="button"
                          tabIndex={0}
                          onClick={() => toggleExpand(mainId)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              toggleExpand(mainId);
                            }
                          }}
                          aria-expanded={expanded}
                          aria-controls={panelId}
                          aria-label={
                            expanded
                              ? `Collapse ${main.title || 'main category'}`
                              : `Expand ${main.title || 'main category'}`
                          }
                          className="group flex items-start gap-2 px-3 md:px-4 py-3 hover:bg-gray-50/80 cursor-pointer"
                        >
                          <span
                            className="mt-0.5 p-1.5 rounded-lg text-gray-600 flex-shrink-0"
                            aria-hidden="true"
                          >
                            {expanded ? (
                              <HiChevronDown className="w-5 h-5" />
                            ) : (
                              <HiChevronRight className="w-5 h-5" />
                            )}
                          </span>
                          <ListRowThumb src={main.image} alt="" />
                          <div className="flex-1 min-w-0">
                            <div className="flex flex-wrap items-center gap-2">
                              <h3 className="text-sm font-semibold text-gray-800 truncate">
                                {main.title}
                              </h3>
                              <StatusBadge isActive={main.is_active !== false} />
                              <span className="text-xs text-gray-400">
                                {subs.length} subcategor{subs.length === 1 ? 'y' : 'ies'}
                              </span>
                            </div>
                            {main.description ? (
                              <p className="text-xs text-gray-500 mt-1 line-clamp-2">
                                {main.description}
                              </p>
                            ) : null}
                          </div>
                          <div
                            className="flex items-center gap-2 flex-shrink-0"
                            onClick={(e) => e.stopPropagation()}
                            onKeyDown={(e) => e.stopPropagation()}
                          >
                            <button
                              type="button"
                              onClick={() => openEditMain(main)}
                              className="p-1.5 rounded-lg hover:opacity-90 opacity-0 pointer-events-none group-hover:opacity-100 group-hover:pointer-events-auto focus:opacity-100 focus:pointer-events-auto transition-opacity"
                              style={{ backgroundColor: '#F2E2F7' }}
                              title="Edit"
                              aria-label="Edit main category"
                            >
                              <MdEdit className="w-4 h-4" style={{ color: '#58327E' }} />
                            </button>
                            <button
                              type="button"
                              onClick={() => openMainSoftDelete(main)}
                              disabled={main.is_active === false}
                              className="bg-[#FDDDDD] text-[#F76464] p-1.5 rounded-lg transition-opacity disabled:opacity-40 disabled:cursor-not-allowed opacity-0 pointer-events-none group-hover:opacity-100 group-hover:pointer-events-auto focus:opacity-100 focus:pointer-events-auto group-hover:disabled:opacity-40"
                              title={
                                main.is_active === false
                                  ? 'Already inactive'
                                  : 'Deactivate (cascades to subcategories)'
                              }
                              aria-label="Deactivate main category"
                            >
                              <MdDelete className="w-4 h-4" />
                            </button>
                            <button
                              type="button"
                              onClick={() => openAddSub(main)}
                              disabled={main.is_active === false}
                              title={
                                main.is_active === false
                                  ? 'Cannot add subcategory under an inactive main category'
                                  : 'Add subcategory'
                              }
                              className="inline-flex items-center gap-1 px-2 py-1.5 rounded-lg text-xs font-semibold hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed whitespace-nowrap"
                              style={{ backgroundColor: '#E6F7ED', color: '#60BC7C' }}
                              aria-label="Add subcategory"
                            >
                              <HiPlus className="w-4 h-4 flex-shrink-0" />
                              Sub Category
                            </button>
                          </div>
                        </div>

                        {expanded && (
                          <div
                            id={panelId}
                            className="bg-gray-50 border-t border-gray-100 px-3 md:px-4 py-3 ml-0 md:ml-8"
                          >
                            <div className="flex items-center justify-between mb-2 gap-2">
                              <h4 className="text-xs font-semibold text-gray-600 uppercase tracking-wider">
                                Subcategories
                              </h4>
                              {subs.length > 0 && (
                                <button
                                  type="button"
                                  onClick={() => openSubReorder(main)}
                                  disabled={subReorderSaving}
                                  className="flex items-center gap-1.5 px-2.5 py-1 text-xs font-semibold text-brand bg-brand/10 rounded-lg hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
                                  aria-label={`Reorder subcategories of ${main.title}`}
                                >
                                  <img
                                    src={EditOrderIcon}
                                    alt=""
                                    className="w-4 h-4"
                                  />
                                  Reorder
                                </button>
                              )}
                            </div>
                            {subs.length === 0 ? (
                              <p className="text-sm text-gray-500 py-2">
                                No subcategories
                                {main.is_active !== false ? ' — use + to add one.' : '.'}
                              </p>
                            ) : (
                              <ul className="space-y-2">
                                {subs.map((sub) => (
                                  <li
                                    key={sub._id}
                                    className="group/sub bg-white rounded-lg border border-gray-100 px-3 py-2 flex items-start gap-2"
                                  >
                                    <ListRowThumb src={sub.image} alt="" />
                                    <div className="flex-1 min-w-0">
                                      <div className="flex flex-wrap items-center gap-2">
                                        <span className="text-sm font-medium text-gray-800">
                                          {sub.title}
                                        </span>
                                        <StatusBadge isActive={sub.is_active !== false} />
                                      </div>
                                      {sub.description ? (
                                        <p className="text-xs text-gray-500 mt-0.5 line-clamp-2">
                                          {sub.description}
                                        </p>
                                      ) : null}
                                      {sub.note ? (
                                        <p className="text-[11px] text-gray-400 mt-0.5 line-clamp-2 italic">
                                          {sub.note}
                                        </p>
                                      ) : null}
                                    </div>
                                    <div className="flex items-center gap-1.5 flex-shrink-0">
                                      <button
                                        type="button"
                                        onClick={() => openEditSub(sub, main)}
                                        className="p-1.5 rounded-lg hover:opacity-90 opacity-0 pointer-events-none group-hover/sub:opacity-100 group-hover/sub:pointer-events-auto focus:opacity-100 focus:pointer-events-auto transition-opacity"
                                        style={{ backgroundColor: '#F2E2F7' }}
                                        title="Edit subcategory"
                                        aria-label="Edit subcategory"
                                      >
                                        <MdEdit className="w-4 h-4" style={{ color: '#58327E' }} />
                                      </button>
                                      <button
                                        type="button"
                                        onClick={() => openSubSoftDelete(sub)}
                                        disabled={sub.is_active === false}
                                        className="bg-[#FDDDDD] text-[#F76464] p-1.5 rounded-lg disabled:opacity-40 disabled:cursor-not-allowed opacity-0 pointer-events-none group-hover/sub:opacity-100 group-hover/sub:pointer-events-auto focus:opacity-100 focus:pointer-events-auto transition-opacity group-hover/sub:disabled:opacity-40"
                                        title={
                                          sub.is_active === false
                                            ? 'Already inactive'
                                            : 'Deactivate subcategory'
                                        }
                                        aria-label="Deactivate subcategory"
                                      >
                                        <MdDelete className="w-4 h-4" />
                                      </button>
                                    </div>
                                  </li>
                                ))}
                              </ul>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Main Category Form Modal */}
      {showMainForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div
            className="bg-white rounded-lg shadow-xl max-w-lg w-full max-h-[90vh] flex flex-col"
            role="dialog"
            aria-modal="true"
            aria-labelledby="main-form-title"
          >
            <div className="px-6 py-4 border-b border-gray-200 flex-shrink-0">
              <h2 id="main-form-title" className="text-lg font-bold text-gray-800">
                {mainEditing ? 'Edit Main Category' : 'Add Main Category'}
              </h2>
            </div>
            <div className="px-6 py-4 space-y-4 overflow-y-auto flex-1">
              {mainDetailLoading && (
                <p className="text-sm text-gray-500" role="status">
                  Loading latest details…
                </p>
              )}
              {mainFormGeneralError && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm whitespace-pre-line">
                  {mainFormGeneralError}
                </div>
              )}
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Title <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={mainForm.title}
                  onChange={(e) => {
                    setMainForm({ ...mainForm, title: e.target.value });
                    if (mainFormErrors.title) setMainFormErrors({ ...mainFormErrors, title: '' });
                  }}
                  className={`w-full px-3 py-2 text-sm border rounded-lg focus:outline-none ${
                    mainFormErrors.title
                      ? 'border-red-300'
                      : 'border-gray-300 focus:border-gray-500'
                  }`}
                />
                {mainFormErrors.title && (
                  <p className="mt-1 text-xs text-red-600">{mainFormErrors.title}</p>
                )}
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Description</label>
                <textarea
                  value={mainForm.description}
                  onChange={(e) => setMainForm({ ...mainForm, description: e.target.value })}
                  rows={3}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none resize-none focus:border-gray-500"
                />
              </div>
              <CategoryImageField
                id="main-category-image"
                resetKey={mainImageResetKey}
                value={mainForm.image}
                error={mainFormErrors.image}
                disabled={mainSaving || mainDetailLoading}
                onUploadingChange={handleMainImageUploading}
                onChange={(next) => {
                  setMainForm((prev) => ({ ...prev, image: next }));
                  if (mainFormErrors.image) setMainFormErrors((prev) => ({ ...prev, image: '' }));
                }}
                onClear={() => {
                  setMainForm((prev) => ({ ...prev, image: '' }));
                  if (mainFormErrors.image) setMainFormErrors((prev) => ({ ...prev, image: '' }));
                }}
              />
              {mainEditing &&
                mainSelected?.is_active !== false &&
                mainForm.is_active === false && (
                  <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-2">
                    Deactivating this main category will also deactivate all of its subcategories
                    (server-side cascade). You will be asked to confirm before saving.
                  </p>
                )}
              {mainEditing &&
                mainSelected?.is_active === false &&
                mainForm.is_active === true && (
                  <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-2">
                    Reactivating a main category does not automatically reactivate its
                    subcategories.
                  </p>
                )}
            </div>
            <div className="px-6 py-4 flex items-center justify-between gap-3 border-t border-gray-200 flex-shrink-0">
              <div className="flex items-center gap-3">
                <span className="text-xs font-medium text-gray-700">Active</span>
                <button
                  type="button"
                  role="switch"
                  aria-checked={mainForm.is_active}
                  onClick={() => setMainForm({ ...mainForm, is_active: !mainForm.is_active })}
                  disabled={mainSaving || mainDetailLoading || mainImageUploading}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors disabled:opacity-50 ${
                    mainForm.is_active ? 'bg-brand' : 'bg-gray-300'
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      mainForm.is_active ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>
              <div className="flex justify-end gap-3">
                <button
                  type="button"
                  onClick={closeMainForm}
                  disabled={mainSaving || mainImageUploading}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleMainSave}
                  disabled={mainSaving || mainDetailLoading || mainImageUploading}
                  className="px-4 py-2 text-sm font-medium text-white bg-brand rounded-lg hover:opacity-90 disabled:opacity-50 flex items-center gap-2"
                >
                  {mainSaving ? (
                    <>
                      <Spinner />
                      {mainEditing ? 'Updating...' : 'Saving...'}
                    </>
                  ) : mainImageUploading ? (
                    'Uploading...'
                  ) : mainEditing ? (
                    'Update'
                  ) : (
                    'Save'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Subcategory Form Modal */}
      {showSubForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div
            className="bg-white rounded-lg shadow-xl max-w-lg w-full max-h-[90vh] flex flex-col"
            role="dialog"
            aria-modal="true"
            aria-labelledby="sub-form-title"
          >
            <div className="px-6 py-4 border-b border-gray-200 flex-shrink-0">
              <h2 id="sub-form-title" className="text-lg font-bold text-gray-800">
                {subEditing ? 'Edit Subcategory' : 'Add Subcategory'}
              </h2>
            </div>
            <div className="px-6 py-4 space-y-4 overflow-y-auto flex-1">
              {subDetailLoading && (
                <p className="text-sm text-gray-500" role="status">
                  Loading latest details…
                </p>
              )}
              {subFormGeneralError && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm whitespace-pre-line">
                  {subFormGeneralError}
                </div>
              )}
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Title <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={subForm.title}
                  onChange={(e) => {
                    setSubForm({ ...subForm, title: e.target.value });
                    if (subFormErrors.title) setSubFormErrors({ ...subFormErrors, title: '' });
                  }}
                  className={`w-full px-3 py-2 text-sm border rounded-lg focus:outline-none ${
                    subFormErrors.title
                      ? 'border-red-300'
                      : 'border-gray-300 focus:border-gray-500'
                  }`}
                />
                {subFormErrors.title && (
                  <p className="mt-1 text-xs text-red-600">{subFormErrors.title}</p>
                )}
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Description</label>
                <textarea
                  value={subForm.description}
                  onChange={(e) => setSubForm({ ...subForm, description: e.target.value })}
                  rows={3}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none resize-none focus:border-gray-500"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Note <span className="text-gray-400 font-normal">(optional)</span>
                </label>
                <textarea
                  value={subForm.note}
                  onChange={(e) => setSubForm({ ...subForm, note: e.target.value })}
                  rows={2}
                  placeholder="e.g. Best when you need a gentle reset mid-day."
                  disabled={subSaving || subDetailLoading}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none resize-none focus:border-gray-500 disabled:opacity-50"
                />
                <p className="mt-1 text-[11px] text-gray-400">
                  Short helper text for this subcategory (e.g. Feeling chip). Clear to remove.
                </p>
              </div>
              <CategoryImageField
                id="sub-category-image"
                resetKey={subImageResetKey}
                value={subForm.image}
                error={subFormErrors.image}
                disabled={subSaving || subDetailLoading}
                onUploadingChange={handleSubImageUploading}
                onChange={(next) => {
                  setSubForm((prev) => ({ ...prev, image: next }));
                  if (subFormErrors.image) setSubFormErrors((prev) => ({ ...prev, image: '' }));
                }}
                onClear={() => {
                  setSubForm((prev) => ({ ...prev, image: '' }));
                  if (subFormErrors.image) setSubFormErrors((prev) => ({ ...prev, image: '' }));
                }}
              />
            </div>
            <div className="px-6 py-4 flex items-center justify-between gap-3 border-t border-gray-200 flex-shrink-0">
              <div className="flex items-center gap-3">
                <span className="text-xs font-medium text-gray-700">Active</span>
                <button
                  type="button"
                  role="switch"
                  aria-checked={subForm.is_active}
                  onClick={() => setSubForm({ ...subForm, is_active: !subForm.is_active })}
                  disabled={subSaving || subDetailLoading || subImageUploading}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors disabled:opacity-50 ${
                    subForm.is_active ? 'bg-brand' : 'bg-gray-300'
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      subForm.is_active ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>
              <div className="flex justify-end gap-3">
                <button
                  type="button"
                  onClick={closeSubForm}
                  disabled={subSaving || subImageUploading}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleSubSave}
                  disabled={subSaving || subDetailLoading || subImageUploading}
                  className="px-4 py-2 text-sm font-medium text-white bg-brand rounded-lg hover:opacity-90 disabled:opacity-50 flex items-center gap-2"
                >
                  {subSaving ? (
                    <>
                      <Spinner />
                      {subEditing ? 'Updating...' : 'Saving...'}
                    </>
                  ) : subImageUploading ? (
                    'Uploading...'
                  ) : subEditing ? (
                    'Update'
                  ) : (
                    'Save'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Main Categories Reorder Modal */}
      {showMainReorderModal && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4 overflow-hidden">
          <div
            className="bg-[#ffffff] w-full max-w-lg rounded-2xl md:ml-0 lg:ml-8 xl:ml-0 p-6 flex flex-col max-h-[90vh] overflow-hidden"
            role="dialog"
            aria-modal="true"
            aria-labelledby="main-reorder-title"
          >
            <div className="flex justify-between items-center mb-4 flex-shrink-0">
              <h2 id="main-reorder-title" className="text-black/60 text-lg font-semibold">
                Reorder Main Categories
              </h2>
            </div>
            <p className="text-sm text-gray-400 mb-4 flex-shrink-0">
              Drag and drop main categories to reorder them. Click <b>Save Order</b> to apply.
              This updates the full category list (search must be cleared first).
            </p>
            <DndContext
              collisionDetection={closestCenter}
              onDragStart={() => {
                document.body.style.overflowX = 'hidden';
              }}
              onDragEnd={(event) => {
                document.body.style.overflowX = '';
                const { active, over } = event;
                if (!over || active.id === over.id) return;
                setReorderMains((prev) => {
                  const oldIndex = prev.findIndex((m) => m._id === active.id);
                  const newIndex = prev.findIndex((m) => m._id === over.id);
                  if (oldIndex < 0 || newIndex < 0) return prev;
                  return arrayMove(prev, oldIndex, newIndex);
                });
              }}
              onDragCancel={() => {
                document.body.style.overflowX = '';
              }}
            >
              <SortableContext
                items={reorderMains.map((m) => m._id)}
                strategy={verticalListSortingStrategy}
              >
                <div
                  className="space-y-3 overflow-y-auto overflow-x-hidden pr-2 flex-1 min-h-0"
                  style={{ maxHeight: '420px' }}
                >
                  {reorderMains.map((item, index) => (
                    <SortableReorderItem
                      key={item._id}
                      id={item._id}
                      title={item.title}
                      image={item.image}
                      index={index}
                    />
                  ))}
                </div>
              </SortableContext>
            </DndContext>
            <div className="flex justify-end gap-3 mt-6 flex-shrink-0">
              <button
                type="button"
                onClick={closeMainReorder}
                disabled={mainReorderSaving}
                className="px-4 py-2 rounded-lg border border-gray-600 text-gray-500 hover:bg-gray-50 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSaveMainOrder}
                disabled={mainReorderSaving || !reorderMains.length}
                className="px-4 py-2 rounded-lg bg-brand text-white hover:opacity-90 disabled:opacity-50 flex items-center gap-2"
              >
                {mainReorderSaving ? (
                  <>
                    <Spinner />
                    Saving...
                  </>
                ) : (
                  'Save Order'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Subcategories Reorder Modal (scoped to parent Main) */}
      {showSubReorderModal && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4 overflow-hidden">
          <div
            className="bg-[#ffffff] w-full max-w-lg rounded-2xl md:ml-0 lg:ml-8 xl:ml-0 p-6 flex flex-col max-h-[90vh] overflow-hidden"
            role="dialog"
            aria-modal="true"
            aria-labelledby="sub-reorder-title"
          >
            <div className="flex justify-between items-center mb-4 flex-shrink-0">
              <h2 id="sub-reorder-title" className="text-black/60 text-lg font-semibold">
                Reorder Subcategories
              </h2>
            </div>
            <p className="text-sm text-gray-400 mb-4 flex-shrink-0">
              Drag and drop subcategories under{' '}
              <b>{reorderSubParent?.title || 'this main category'}</b>. Click{' '}
              <b>Save Order</b> to apply. Parent assignment is not changed.
            </p>
            <DndContext
              collisionDetection={closestCenter}
              onDragStart={() => {
                document.body.style.overflowX = 'hidden';
              }}
              onDragEnd={(event) => {
                document.body.style.overflowX = '';
                const { active, over } = event;
                if (!over || active.id === over.id) return;
                setReorderSubs((prev) => {
                  const oldIndex = prev.findIndex((s) => s._id === active.id);
                  const newIndex = prev.findIndex((s) => s._id === over.id);
                  if (oldIndex < 0 || newIndex < 0) return prev;
                  return arrayMove(prev, oldIndex, newIndex);
                });
              }}
              onDragCancel={() => {
                document.body.style.overflowX = '';
              }}
            >
              <SortableContext
                items={reorderSubs.map((s) => s._id)}
                strategy={verticalListSortingStrategy}
              >
                <div
                  className="space-y-3 overflow-y-auto overflow-x-hidden pr-2 flex-1 min-h-0"
                  style={{ maxHeight: '420px' }}
                >
                  {reorderSubs.map((item, index) => (
                    <SortableReorderItem
                      key={item._id}
                      id={item._id}
                      title={item.title}
                      image={item.image}
                      index={index}
                    />
                  ))}
                </div>
              </SortableContext>
            </DndContext>
            <div className="flex justify-end gap-3 mt-6 flex-shrink-0">
              <button
                type="button"
                onClick={closeSubReorder}
                disabled={subReorderSaving}
                className="px-4 py-2 rounded-lg border border-gray-600 text-gray-500 hover:bg-gray-50 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSaveSubOrder}
                disabled={subReorderSaving || !reorderSubs.length}
                className="px-4 py-2 rounded-lg bg-brand text-white hover:opacity-90 disabled:opacity-50 flex items-center gap-2"
              >
                {subReorderSaving ? (
                  <>
                    <Spinner />
                    Saving...
                  </>
                ) : (
                  'Save Order'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirm deactivate / cascade */}
      {confirmState && confirmState.item && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-[60] flex items-center justify-center p-6">
          <div
            className="bg-white rounded-lg shadow-xl max-w-md w-full"
            role="dialog"
            aria-modal="true"
            aria-labelledby="confirm-title"
          >
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 id="confirm-title" className="text-xl font-bold text-gray-800">
                {confirmState.kind === 'main-cascade'
                  ? 'Deactivate Main Category'
                  : 'Deactivate Subcategory'}
              </h2>
            </div>
            <div className="px-6 py-4 space-y-2">
              {confirmState.kind === 'main-cascade' ? (
                <>
                  <p className="text-gray-700">
                    Deactivate <strong>{confirmState.item.title}</strong>?
                  </p>
                  <p className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg p-3">
                    This cascades on the server: all subcategories under this main will also be
                    set inactive. If the server cannot run a transaction, you will get an error
                    and nothing will change.
                  </p>
                </>
              ) : (
                <p className="text-gray-700">
                  Deactivate subcategory <strong>{confirmState.item.title}</strong>? Session
                  references are not removed.
                </p>
              )}
            </div>
            <div className="px-6 py-4 flex justify-end gap-3">
              <button
                type="button"
                onClick={closeConfirm}
                disabled={confirming}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleConfirm}
                disabled={confirming}
                className="px-4 py-2 text-sm font-medium text-white bg-[#F76464] rounded-lg disabled:opacity-50 flex items-center gap-2"
              >
                {confirming ? (
                  <>
                    <Spinner />
                    Deactivating...
                  </>
                ) : (
                  'Confirm Deactivate'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MeditationMainCategories;
